import { useFormDataQuery } from '@/api/getters';
import { TYPE_OF_DAMAGE } from '@/api/types';
import Card, { CardDeadEndHint } from '@/components/Card';
import { ConditionalRender } from '@/components/ui/ConditionalRender';
import { Hint } from '@/components/ui/Hint';
import { useStepper } from '@/features/v2/lib/stepper';
import InputPostalCode, {
  POSTAL_CODE_NAME,
} from '@/features/v2/vehicle-damage/components/InputPostalCode';
import {
  ACCIDENT_REPORTED_TO_INSURER_NAME,
  ACCIDENT_REPORTED_TO_INSURER_VALUES,
  RadioGroupAccidentReportedToInsurer,
} from '@/features/v2/vehicle-damage/components/RadioGroupAccidentReportedToInsurer';
import {
  HAS_DAMAGE_PHOTOS,
  HAS_DAMAGE_PHOTOS_NAME,
  RadioGroupHasDamagePhotos,
} from '@/features/v2/vehicle-damage/components/RadioGroupHasDamagePhotos';
import { RadioGroupHowWasDamageEstimated } from '@/features/v2/vehicle-damage/components/RadioGroupHowWasDamageEstimated';
import { RadioGroupRiskyArea } from '@/features/v2/vehicle-damage/components/RadioGroupRiskyArea';
import {
  RadioGroupTypeOfDamage,
  TYPE_OF_DAMAGE_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupTypeOfDamage';
import {
  RadioGroupVehicleOccupantInjury,
  VEHICLE_OCCUPANT_INJURY_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleOccupantInjury';
import {
  RadioGroupVehicleValueBeforeCrash,
  VEHICLE_VALUE_BEFORE_CRASH,
  VEHICLE_VALUE_BEFORE_CRASH_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleValueBeforeCrash';
import {
  PROVINCE,
  PROVINCE_NAME,
  SelectAccidentProvince,
} from '@/features/v2/vehicle-damage/components/SelectAccidentProvince';
import {
  INSURANCE_COMPANY_NAME,
  SelectInsuranceCompany,
} from '@/features/v2/vehicle-damage/components/SelectInsuranceCompany';
import { VehicleDamageSchemaDeadEndsStepOne } from '@/features/v2/vehicle-damage/stepsSchema';

import { useLingui } from '@lingui/react';

export const SurveyOne = () => {
  const { innerStep, step } = useStepper();
  const { i18n } = useLingui();
  const { data } = useFormDataQuery();
  const withPostalCode = !data?.product?.additional_info?.postal_code;

  return (
    <>
      <Card
        isDeadEnd={
          VehicleDamageSchemaDeadEndsStepOne.noAccidentReportedToInsurer
            .condition
        }
      >
        <RadioGroupAccidentReportedToInsurer />
        <ConditionalRender
          when={(val) =>
            val[ACCIDENT_REPORTED_TO_INSURER_NAME] ===
              ACCIDENT_REPORTED_TO_INSURER_VALUES.YES_MY_INSURE ||
            val[ACCIDENT_REPORTED_TO_INSURER_NAME] ===
              ACCIDENT_REPORTED_TO_INSURER_VALUES.YES_PERPETRATOR
          }
        >
          <SelectInsuranceCompany />
          <ConditionalRender when={(val) => !!val[INSURANCE_COMPANY_NAME]}>
            <RadioGroupVehicleOccupantInjury />
            <ConditionalRender
              when={(val) =>
                withPostalCode && !!val[VEHICLE_OCCUPANT_INJURY_NAME]
              }
            >
              {withPostalCode && <InputPostalCode />}
            </ConditionalRender>
          </ConditionalRender>
        </ConditionalRender>
        <CardDeadEndHint variant="primary">
          <Hint.Title>
            {i18n.t(
              'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
            )}
          </Hint.Title>
          <Hint.Description>
            <p>
              {i18n.t(
                'Do weryfikacji szkody potrzebujemy kosztorysu od ubezpieczyciela. Zgłoś szkodę, zaczekaj na kosztorys i decyzję, a następnie wróć do nas i dokończ formularz.'
              )}
            </p>
            <p>
              {i18n.t(
                'Uważaj na ugodę w rozmowie z ubezpieczycielem – w procesie likwidacji szkody często wymagają potwierdzenia wysokości wypłaty. Cechy charakterystyczne dla ugód:'
              )}
            </p>
            <ul className="ml-5.5 list-disc">
              <li>
                {i18n.t(
                  'ugodę można zawrzeć telefonicznie, papierowo, mailowo lub przez aplikację,'
                )}
              </li>
              <li>
                {i18n.t(
                  'w procesie likwidacji szkody ubezpieczyciel często wymaga potwierdzenia wysokości wypłaty; bezpiecznym rozwiązaniem jest powiedzenie "akceptuję kwotę bezsporną z kosztorysu"'
                )}
              </li>
              <li>
                {i18n.t(
                  'zawarcie ugody uniemożliwia dodanie uszkodzeń, które nie zostały uwzględnione w kosztorysie od ubezpieczyciela,'
                )}
              </li>
              <li>
                {i18n.t(
                  'ugoda często dotyczy okrągłej kwoty, np. na kosztorys z kwotą 2458,14 zł, ubezpieczyciel zaproponuje ugodę na 2500,00 zł.'
                )}
              </li>
            </ul>
          </Hint.Description>
        </CardDeadEndHint>
      </Card>
      <ConditionalRender
        when={(val) =>
          !!val[VEHICLE_OCCUPANT_INJURY_NAME] &&
          (!withPostalCode || val[POSTAL_CODE_NAME]?.length >= 6)
        }
      >
        <Card>
          <SelectAccidentProvince />
          <ConditionalRender
            when={(val) => val[PROVINCE_NAME] === PROVINCE.KUJAWSKO_POMORSKIE}
          >
            <RadioGroupRiskyArea />
          </ConditionalRender>
        </Card>
      </ConditionalRender>
      {(innerStep >= 1 || step > 0) && (
        <>
          <Card
            isDeadEnd={
              VehicleDamageSchemaDeadEndsStepOne
                .radioGroupVehicleValueBeforeCrash.condition
            }
          >
            <RadioGroupTypeOfDamage />
            <ConditionalRender
              when={(val) =>
                val[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_AC ||
                val[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_OC
              }
            >
              <RadioGroupVehicleValueBeforeCrash />
            </ConditionalRender>
            <CardDeadEndHint variant="primary">
              <Hint.Title>
                {i18n.t(
                  'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
                )}
              </Hint.Title>
              <Hint.Description className="caption-large font-jakarta lg:text-xs">
                {i18n.t(
                  'W przypadku pojazdów wycenianych przed szkodą całkowitą na kwotę poniżej 10 000 PLN, nasze możliwości działania są ograniczone. Uprzejmie prosimy o zakończenie formularza, aby uniknąć konieczności kontaktu telefonicznego.'
                )}
              </Hint.Description>
            </CardDeadEndHint>
          </Card>
          <ConditionalRender
            when={(val) =>
              val[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.PART_AC ||
              val[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.PART_OC ||
              ((val[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_AC ||
                val[TYPE_OF_DAMAGE_NAME] === TYPE_OF_DAMAGE.FULL_OC) &&
                val[VEHICLE_VALUE_BEFORE_CRASH_NAME] ===
                  VEHICLE_VALUE_BEFORE_CRASH.UPPER_10k)
            }
          >
            <Card>
              <RadioGroupHasDamagePhotos />
              <ConditionalRender
                when={(val) =>
                  val[HAS_DAMAGE_PHOTOS_NAME] === HAS_DAMAGE_PHOTOS.NO
                }
              >
                <RadioGroupHowWasDamageEstimated />
              </ConditionalRender>
            </Card>
          </ConditionalRender>
        </>
      )}
    </>
  );
};
